import { ReactNode } from 'react';
import { AlertTriangle, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { Typography } from '@/components/ui/typography';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose
} from '@/components/ui/sheet';

import { OrderSliderForms } from '../model/CartModel';
import {
  DeleteConfirmation,
  SliderState,
  AssetEditModalState,
  PropertyCartItem,
} from '../types/cart.types';

import ServiceAccessSlider from '../service-access-slider/ServiceAccessSlider';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import AssetCartEditModal from './AssetCartEditModal';

type CartDialogsProps = {
  // Slider state + handlers
  sliderState: SliderState;
  onSliderClose: () => void;
  onSliderContinueToAccess: (data: PropertyCartItem) => void;
  onSliderDone: (data: PropertyCartItem) => void;
  onSliderBackToService: (data: PropertyCartItem) => void;

  // Asset edit modal state + handlers
  assetEditModal: AssetEditModalState;
  onAssetEditClose: () => void;
  updateAssetGroup: (p: { lineItemUUID?: string; lineAssetId: string; lineSlug: string; removedRefIds: string[]; note: string }) => Promise<void>;
  removeAssetItem: (lineItemUUID: string | undefined, assetId: string, slug: string) => Promise<void>;

  // Delete confirmation state + handlers
  deleteConfirmation: DeleteConfirmation;
  onConfirmDelete: () => void;
  onCancelDelete: () => void;

  // Partial order modal state + handlers
  showPartialOrderModal: boolean;
  onPartialOrderChange: (open: boolean) => void;
  emptyServiceCount: number;
  onPartialOrderContinue: () => void;
};

const getSliderTitle = (type: OrderSliderForms): string => {
  if (type === OrderSliderForms.SERVICES) return 'Services';
  if (type === OrderSliderForms.ACCESSINFO) return 'Appointment Information';
  return '';
};

const getDeleteTitle = (itemType: string): ReactNode => {
  if (itemType === 'all') return 'Clear Cart';
  return (
    <span className="inline-flex items-center gap-2">
      <AlertTriangle className="h-5 w-5 text-warning" />
      <Typography variant="heading-lg" className="font-medium">
        Warning
      </Typography>
    </span>
  );
};

const getDeleteMessage = (confirmation: DeleteConfirmation): string => {
  if (confirmation.itemType === 'all') {
    return 'Are you sure you want to remove all items from your cart? This action cannot be undone.';
  }
  return `Are you sure you want to remove ${confirmation.itemLabel} from your cart?`;
};

const CartDialogs = ({
  sliderState,
  onSliderClose,
  onSliderContinueToAccess,
  onSliderDone,
  onSliderBackToService,
  assetEditModal,
  onAssetEditClose,
  updateAssetGroup,
  removeAssetItem,
  deleteConfirmation,
  onConfirmDelete,
  onCancelDelete,
  showPartialOrderModal,
  onPartialOrderChange,
  emptyServiceCount,
  onPartialOrderContinue
}: CartDialogsProps) => {
  const sliderTitle = getSliderTitle(sliderState.type);

  return (
    <>
      {/* Service / Access slider */}
      <Sheet
        open={sliderState.show}
        onOpenChange={(open) => !open && onSliderClose()}
      >
        <SheetContent
          side="right"
          className="flex flex-col p-0 gap-0 w-[min(1120px,94vw)] sm:max-w-none [&>button]:hidden"
        >
          <SheetHeader className="shrink-0 flex flex-row items-center justify-between space-y-0 border-b border-border px-5 py-3">
            <div className="min-w-0 flex-1">
              <SheetTitle className="truncate text-lg font-medium">
                {sliderState.data?.formattedAddress || sliderTitle}
              </SheetTitle>
              <SheetDescription className="text-sm text-muted-foreground">
                {sliderTitle}
              </SheetDescription>
            </div>
            <SheetClose className="-mr-2 inline-flex cursor-pointer items-center justify-center rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring max-lg:h-11 max-lg:w-11">
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </SheetClose>
          </SheetHeader>
          <div className="flex-1 min-h-0 flex flex-col">
            {sliderState.data && (
              <ServiceAccessSlider
                type={sliderState.type}
                data={sliderState.data}
                onContinueAction={onSliderContinueToAccess}
                onDoneAction={onSliderDone}
                onServiceAction={onSliderBackToService}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Asset edit modal */}
      {assetEditModal.show && assetEditModal.item && (
        <AssetCartEditModal
          row={assetEditModal.item}
          updateAssetGroup={updateAssetGroup}
          removeAssetItem={removeAssetItem}
          onClose={onAssetEditClose}
        />
      )}

      {/* Delete confirmation */}
      <ConfirmDeleteDialog
        show={deleteConfirmation.show}
        title={getDeleteTitle(deleteConfirmation.itemType)}
        message={getDeleteMessage(deleteConfirmation)}
        confirmButtonText={
          deleteConfirmation.itemType === 'all' ? 'Clear Cart' : 'Remove'
        }
        onConfirm={onConfirmDelete}
        onCancel={onCancelDelete}
      />

      {/* Partial order modal */}
      {showPartialOrderModal && (
        <Modal
          open={showPartialOrderModal}
          onOpenChange={onPartialOrderChange}
          title="Place Partial Order?"
        >
          <div className="flex flex-col gap-1">
            <Typography
              as="p"
              variant="body"
              className="border-b border-border px-5 py-2.5"
            >
              {emptyServiceCount === 1
                ? 'There is 1 property without services selected.'
                : `There are ${emptyServiceCount} properties without services selected.`}{' '}
              You can place the order now for the properties that have services
              and come back later for the rest.
            </Typography>
            <div className="flex flex-col-reverse gap-3 p-5 sm:flex-row sm:items-center sm:justify-end">
              <Button
                variant="outline"
                onClick={() => onPartialOrderChange(false)}
                className="w-full sm:w-auto"
              >
                Add Services
              </Button>
              <Button
                onClick={onPartialOrderContinue}
                className="w-full sm:w-auto"
              >
                Continue &amp; Place Order
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default CartDialogs;
